import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import API from "../utils/API";
import "./addTaskModal.css";
import "./taskDetailModal.css";

const PRIORITIES = ["Low", "Medium", "High", "Urgent"];

function todayPT() {
    return new Date().toLocaleDateString("en-CA", { timeZone: "America/Los_Angeles" });
}

function formatDate(value) {
    return new Date(`${value}T00:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function formatStamp(value) {
    return new Date(value).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

/** Add / edit form for one step: description and optional due date. */
function StepForm({ initial, maxDate, busy, submitLabel, onSubmit, onCancel }) {
    const [text, setText] = useState(initial?.step_text || "");
    const [due, setDue] = useState(initial?.due_date || "");

    const [error, setError] = useState("");

    const submit = () => {
        if (!text.trim()) return;
        if (maxDate && due > maxDate) {
            setError(`A step can't be due after the task's due date (${formatDate(maxDate)}).`);
            return;
        }
        setError("");
        onSubmit({ step_text: text.trim(), due_date: due || null });
    };

    return (
        <div className="tdm-step-form">
            <input
                className="form-control form-control-sm"
                placeholder="Describe the step..."
                maxLength={300}
                value={text}
                onChange={e => setText(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter") submit(); }}
                autoFocus
            />
            <div className="tdm-step-form-row">
                <div className="tdm-step-form-field">
                    <label className="tdm-mini-label">Due date (optional)</label>
                    <input type="date" className="form-control form-control-sm" max={maxDate || undefined} value={due} onChange={e => setDue(e.target.value)} />
                </div>
                <div className="tdm-step-form-buttons">
                    <button type="button" className="btn tdm-btn tdm-btn--ghost" onClick={onCancel} disabled={busy}>Cancel</button>
                    <button type="button" className="btn tdm-btn" onClick={submit} disabled={busy || !text.trim()}>{submitLabel}</button>
                </div>
            </div>
            {maxDate && <div className="tdm-field-note">Note: Can't be later than the task's due date ({formatDate(maxDate)}).</div>}
            {error && <div className="atm-error">{error}</div>}
        </div>
    );
}

/**
 * Detail for one Workspace task, as the Dashboard widget lists it:
 * { id, team_id, task_text, due_date, priority, bill_id?, Bill?, TeamAccount: { team_name }, rulemaking_ref?, rulemaking_title?,
 *   rulemaking_url?, assigned_to_user_id, assigned_by_user_id, assigned_by_name? }
 *
 * The assignee can log progress notes. A task they gave themselves is theirs to edit here; one someone else
 * assigned can't be changed, so they request a new due date or priority and the assigner is notified.
 * The assignee, whoever assigned it, and account managers (canManage) also break it into steps; everyone else reads.
 * onUpdated(fields) reports the saved task fields back so the caller's list stays current, and
 * onStepsChanged({ step_count, steps_done }) does the same for the step tally.
 */
export default function TaskDetailModal({ task, userId, canManage, onClose, onUpdated, onStepsChanged }) {
    const selfAssigned = !!userId && task.assigned_to_user_id === userId && task.assigned_by_user_id === userId;
    const isAssignee = !!userId && task.assigned_to_user_id === userId;
    const overdue = task.due_date && task.due_date < todayPT();

    const [mode, setMode] = useState("view"); // view | edit | request
    const [notes, setNotes] = useState([]);
    const [loadingNotes, setLoadingNotes] = useState(true);
    const [steps, setSteps] = useState([]);
    const [loadingSteps, setLoadingSteps] = useState(true);
    const [editingStepId, setEditingStepId] = useState(null);
    const [addingStep, setAddingStep] = useState(false);
    const [stepBusy, setStepBusy] = useState(false);
    const [stepError, setStepError] = useState("");
    const [noteText, setNoteText] = useState("");
    const [draft, setDraft] = useState({ task_text: "", due_date: "", priority: "", reason: "" });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [confirmation, setConfirmation] = useState("");

    useEffect(() => {
        let cancelled = false;
        API.getTaskNotes(task.team_id, task.id, userId)
            .then(res => { if (!cancelled) setNotes(res.data); })
            .catch(() => {})
            .finally(() => { if (!cancelled) setLoadingNotes(false); });
        return () => { cancelled = true; };
    }, [task.team_id, task.id, userId]);

    useEffect(() => {
        let cancelled = false;
        API.getTaskSteps(task.team_id, task.id, userId)
            .then(res => { if (!cancelled) setSteps(res.data); })
            .catch(() => {})
            .finally(() => { if (!cancelled) setLoadingSteps(false); });
        return () => { cancelled = true; };
    }, [task.team_id, task.id, userId]);

    const applySteps = next => {
        setSteps(next);
        onStepsChanged?.({ step_count: next.length, steps_done: next.filter(s => s.completed).length });
    };

    const runStepAction = async action => {
        setStepBusy(true);
        setStepError("");
        try {
            await action();
        } catch (err) {
            setStepError(err.response?.data?.message || "Something went wrong. Please try again.");
        } finally {
            setStepBusy(false);
        }
    };

    const addStep = fields => runStepAction(async () => {
        const res = await API.addTaskStep(task.team_id, task.id, { ...fields, created_by_user_id: userId });
        applySteps([...steps, res.data]);
        setAddingStep(false);
    });

    const saveStep = (step, fields) => runStepAction(async () => {
        const res = await API.updateTaskStep(task.team_id, task.id, step.id, { ...fields, requesting_user_id: userId });
        applySteps(steps.map(s => (s.id === step.id ? res.data : s)));
        setEditingStepId(null);
    });

    const removeStep = step => runStepAction(async () => {
        await API.deleteTaskStep(task.team_id, task.id, step.id, userId);
        applySteps(steps.filter(s => s.id !== step.id));
    });

    const openMode = next => {
        setDraft({ task_text: task.task_text, due_date: task.due_date || "", priority: task.priority || "", reason: "" });
        setError("");
        setConfirmation("");
        setMode(next);
    };

    const failure = err => err.response?.data?.message || "Something went wrong. Please try again.";

    const saveEdit = async () => {
        if (!draft.task_text.trim()) {
            setError("Task description is required.");
            return;
        }
        setSaving(true);
        setError("");
        const body = {
            requesting_user_id: userId,
            task_text: draft.task_text.trim(),
            due_date: draft.due_date || null,
            priority: draft.priority || null,
        };
        try {
            if (task.bill_id) await API.updateBillTask(task.team_id, task.bill_id, task.id, body);
            else await API.updateTeamTask(task.team_id, task.id, body);
            onUpdated({ task_text: body.task_text, due_date: body.due_date, priority: body.priority });
            setMode("view");
        } catch (err) {
            setError(failure(err));
        } finally {
            setSaving(false);
        }
    };

    const sendRequest = async () => {
        const dueChanged = draft.due_date && draft.due_date !== (task.due_date || "");
        const priorityChanged = draft.priority && draft.priority !== (task.priority || "");
        if (!dueChanged && !priorityChanged) {
            setError("Choose a new due date or priority to request.");
            return;
        }
        setSaving(true);
        setError("");
        try {
            const res = await API.requestTaskChange(task.team_id, task.id, {
                requesting_user_id: userId,
                due_date: dueChanged ? draft.due_date : undefined,
                priority: priorityChanged ? draft.priority : undefined,
                reason: draft.reason,
            });
            setNotes(prev => [...prev, res.data.note]);
            setConfirmation(`Request sent to ${res.data.notified || "the person who assigned this task"}.`);
            setMode("view");
        } catch (err) {
            setError(failure(err));
        } finally {
            setSaving(false);
        }
    };

    const addNote = async () => {
        if (!noteText.trim()) return;
        setSaving(true);
        setError("");
        try {
            const res = await API.addTaskNote(task.team_id, task.id, { note_text: noteText, created_by_user_id: userId });
            setNotes(prev => [...prev, res.data]);
            setNoteText("");
        } catch (err) {
            setError(failure(err));
        } finally {
            setSaving(false);
        }
    };

    const assignerLabel = selfAssigned ? "You" : (task.assigned_by_name || (task.assigned_by_user_id ? "A teammate" : "—"));
    const canNote = isAssignee || task.assigned_by_user_id === userId || !!canManage;
    const stepsDone = steps.filter(s => s.completed).length;
    const editing = mode === "edit";

    return (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: "rgba(0,0,0,0.45)" }} onClick={onClose}>
            <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable atm-dialog tdm-dialog" onClick={e => e.stopPropagation()}>
                <div className="modal-content">
                    <div className="modal-header tdm-header">
                        {task.TeamAccount?.team_name && <div className="tdm-eyebrow">{task.TeamAccount.team_name}</div>}
                        {editing ? (
                            <textarea
                                className="form-control form-control-sm"
                                rows={3}
                                maxLength={500}
                                value={draft.task_text}
                                onChange={e => setDraft(d => ({ ...d, task_text: e.target.value }))}
                                aria-label="Task description"
                                autoFocus
                            />
                        ) : (
                            <h5 className="tdm-heading">{task.task_text}</h5>
                        )}
                        {!editing && (
                            <div className="tdm-chips">
                                {task.priority && <span className={`tdm-priority tdm-priority--${task.priority.toLowerCase()}`}>{task.priority}</span>}
                                {task.due_date && (
                                    <span className={`tdm-chip${overdue ? " tdm-chip--overdue" : ""}`}>
                                        <i className={`bi ${overdue ? "bi-exclamation-circle-fill" : "bi-calendar3"}`} />
                                        {overdue ? "Overdue · " : "Due "}{formatDate(task.due_date)}
                                    </span>
                                )}
                                {selfAssigned && <span className="tdm-chip"><i className="bi bi-person-check" />Assigned to yourself</span>}
                            </div>
                        )}
                    </div>

                    <div className="modal-body atm-body">
                        <section className="tdm-section">
                            <div className="tdm-grid">
                                {task.Bill && (
                                    <div className="tdm-cell tdm-cell--wide">
                                        <span className="atm-label">Legislation</span>
                                        <Link to={`/legislation/bill/${task.Bill.bill_id}`} className="tdm-link">
                                            <span className="tdm-link-num">{task.Bill.bill_type} {task.Bill.bill_number}</span>
                                            {task.Bill.title && <span className="tdm-link-title">{task.Bill.title}</span>}
                                        </Link>
                                    </div>
                                )}
                                {task.rulemaking_ref && (
                                    <div className="tdm-cell tdm-cell--wide">
                                        <span className="atm-label">Rulemaking</span>
                                        {task.rulemaking_url ? (
                                            <a href={task.rulemaking_url} target="_blank" rel="noopener noreferrer" className="tdm-link">
                                                <span className="tdm-link-title">{task.rulemaking_title || "Open rulemaking item"}</span>
                                                <i className="bi bi-box-arrow-up-right" />
                                            </a>
                                        ) : (
                                            <span className="tdm-value">{task.rulemaking_title || "Rulemaking item"}</span>
                                        )}
                                    </div>
                                )}
                                <div className="tdm-cell">
                                    <span className="atm-label">Due date</span>
                                    {mode === "view" ? (
                                        task.due_date
                                            ? <span className={`tdm-value${overdue ? " tdm-overdue" : ""}`}>{formatDate(task.due_date)}</span>
                                            : <span className="tdm-value tdm-none">None</span>
                                    ) : (
                                        <input type="date" className="form-control form-control-sm" value={draft.due_date} onChange={e => setDraft(d => ({ ...d, due_date: e.target.value }))} />
                                    )}
                                </div>
                                <div className="tdm-cell">
                                    <span className="atm-label">Priority</span>
                                    {mode === "view" ? (
                                        task.priority
                                            ? <span className="tdm-value">{task.priority}</span>
                                            : <span className="tdm-value tdm-none">None</span>
                                    ) : (
                                        <select className="form-select form-select-sm" value={draft.priority} onChange={e => setDraft(d => ({ ...d, priority: e.target.value }))}>
                                            <option value="">None</option>
                                            {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
                                        </select>
                                    )}
                                </div>
                                <div className="tdm-cell">
                                    <span className="atm-label">Assigned by</span>
                                    <span className="tdm-value">{assignerLabel}</span>
                                </div>
                            </div>

                            {mode === "request" && (
                                <div className="tdm-panel">
                                    <div className="tdm-panel-title">Request a change</div>
                                    <p className="tdm-hint">{task.assigned_by_name || "The person who assigned this task"} will be notified. Pick the new due date or priority you need.</p>
                                    <input
                                        className="form-control form-control-sm"
                                        placeholder="Reason (optional)"
                                        maxLength={1000}
                                        value={draft.reason}
                                        onChange={e => setDraft(d => ({ ...d, reason: e.target.value }))}
                                    />
                                </div>
                            )}

                            {error && <div className="atm-error mt-2">{error}</div>}
                            {confirmation && mode === "view" && <div className="tdm-confirm"><i className="bi bi-check-circle-fill" /> {confirmation}</div>}

                            <div className="tdm-actions">
                                {mode === "view" && selfAssigned && (
                                    <button type="button" className="btn tdm-btn" onClick={() => openMode("edit")}>
                                        Edit task
                                    </button>
                                )}
                                {mode === "view" && isAssignee && !selfAssigned && (
                                    <button type="button" className="btn tdm-btn" onClick={() => openMode("request")}>
                                        Request change
                                    </button>
                                )}
                                {mode !== "view" && (
                                    <>
                                        <button type="button" className="btn tdm-btn tdm-btn--ghost" onClick={() => { setMode("view"); setError(""); }} disabled={saving}>Cancel</button>
                                        <button type="button" className="btn tdm-btn" onClick={editing ? saveEdit : sendRequest} disabled={saving}>
                                            {saving ? "Saving..." : editing ? "Save changes" : "Send request"}
                                        </button>
                                    </>
                                )}
                            </div>
                        </section>

                        <section className="tdm-section">
                            <div className="tdm-section-head">
                                <span className="atm-label">Steps</span>
                                <span className="tdm-section-head-end">
                                    {steps.length > 0 && <span className="tdm-section-meta">{stepsDone} of {steps.length} done</span>}
                                    {canNote && editingStepId === null && !addingStep && (
                                        <button type="button" className="btn tdm-btn" onClick={() => setAddingStep(true)}>+ Add Step</button>
                                    )}
                                </span>
                            </div>
                            {steps.length > 0 && (
                                <div className="tdm-progress" aria-hidden="true">
                                    <div className="tdm-progress-bar" style={{ width: `${(stepsDone / steps.length) * 100}%` }} />
                                </div>
                            )}
                            {loadingSteps ? (
                                <div className="tdm-hint">Loading...</div>
                            ) : steps.length === 0 ? (
                                <div className="tdm-hint">{canNote ? "Break this task into steps to track the work." : "No steps yet."}</div>
                            ) : (
                                <ul className="tdm-steps">
                                    {steps.map(step => {
                                        const stepOverdue = step.due_date && !step.completed && step.due_date < todayPT();
                                        return (
                                            <li key={step.id} className={`tdm-step${step.completed ? " tdm-step--done" : ""}`}>
                                                {editingStepId === step.id ? (
                                                    <StepForm
                                                        initial={step}
                                                        maxDate={task.due_date}
                                                        busy={stepBusy}
                                                        submitLabel="Save"
                                                        onSubmit={fields => saveStep(step, fields)}
                                                        onCancel={() => setEditingStepId(null)}
                                                    />
                                                ) : (
                                                    <>
                                                        <input
                                                            type="checkbox"
                                                            className="tdm-step-check"
                                                            checked={step.completed}
                                                            disabled={!canNote || stepBusy}
                                                            onChange={() => saveStep(step, { completed: !step.completed })}
                                                            aria-label={`Mark "${step.step_text}" ${step.completed ? "not done" : "done"}`}
                                                        />
                                                        <div className="tdm-step-main">
                                                            <span className="tdm-step-text">{step.step_text}</span>
                                                            {step.due_date && (
                                                                <span className="tdm-step-meta">
                                                                    <span className={stepOverdue ? "tdm-overdue" : undefined}>
                                                                        <i className="bi bi-calendar3" /> {formatDate(step.due_date)}
                                                                    </span>
                                                                </span>
                                                            )}
                                                        </div>
                                                        {canNote && (
                                                            <span className="tdm-step-tools">
                                                                <button type="button" className="tdm-icon-btn" onClick={() => setEditingStepId(step.id)} disabled={stepBusy} aria-label="Edit step"><i className="bi bi-pencil" /></button>
                                                                <button type="button" className="tdm-icon-btn tdm-icon-btn--danger" onClick={() => removeStep(step)} disabled={stepBusy} aria-label="Delete step"><i className="bi bi-trash" /></button>
                                                            </span>
                                                        )}
                                                    </>
                                                )}
                                            </li>
                                        );
                                    })}
                                </ul>
                            )}
                            {stepError && <div className="atm-error mt-2">{stepError}</div>}
                            {canNote && editingStepId === null && addingStep && (
                                <StepForm maxDate={task.due_date} busy={stepBusy} submitLabel="Add step" onSubmit={addStep} onCancel={() => { setAddingStep(false); setStepError(""); }} />
                            )}
                        </section>

                        <section className="tdm-section">
                            <span className="atm-label">Progress notes</span>
                            {loadingNotes ? (
                                <div className="tdm-hint">Loading...</div>
                            ) : notes.length === 0 ? (
                                <div className="tdm-hint">No notes yet.</div>
                            ) : (
                                <ul className="tdm-notes">
                                    {notes.map(n => (
                                        <li key={n.id} className="tdm-note">
                                            <div className="tdm-note-meta">
                                                <span className="tdm-note-author">{n.created_by_user_id === userId ? "You" : (n.author_name || "Someone")}</span>
                                                {n.kind === "change_request" && <span className="tdm-note-badge">Change request</span>}
                                                <span className="tdm-note-date">{formatStamp(n.created_at)}</span>
                                            </div>
                                            <div className="tdm-note-text">{n.note_text}</div>
                                        </li>
                                    ))}
                                </ul>
                            )}
                            {canNote && (
                                <div className="tdm-composer">
                                    <textarea
                                        className="form-control form-control-sm"
                                        rows={2}
                                        maxLength={2000}
                                        placeholder="Add a progress note..."
                                        value={noteText}
                                        onChange={e => setNoteText(e.target.value)}
                                    />
                                    <button type="button" className="btn tdm-btn" onClick={addNote} disabled={saving || !noteText.trim()}>Add note</button>
                                </div>
                            )}
                        </section>
                    </div>

                    <div className="modal-footer atm-footer">
                        <button type="button" className="btn atm-cancel-btn" onClick={onClose}>Close</button>
                    </div>
                </div>
            </div>
        </div>
    );
}
